import { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import tw from 'twin.macro';

import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';
import { Input } from '@auth_module/components/Input';

import { registerUser, login, fetchUserInfo } from '@auth_module/services';
import { IRequestError } from '@configs/axios-instances';
import { useAuthContext } from '@auth_module/context/AuthContext';

const FlexRow = tw.div`flex flex-col md:flex-row justify-center items-start gap-y-4 md:gap-y-0 md:gap-x-4`;
const StyledP = tw.p`text-sm`;
const StyledForm = tw.form`flex flex-col w-full gap-[12px]`;
const TermsP = tw.p`text-xs text-accent-1`;
const TermsButton = tw.span`text-accent-1 underline text-xs cursor-pointer`;

const RegisterFormHeader = ({ email }: { email: string }) => {
  const { t } = useTranslation();

  return (
    <>
      <h3 tw='text-2xl text-accent-2'>{t('auth.register_form.title')}</h3>
      <Spacer direction='vertical' size='xxs' />
      <StyledP>{email}</StyledP>
    </>
  );
};

const RegisterErrorInfo = ({ error }: { error: string }) => {
  return (
    <>
      <StyledP>
        <Trans i18nKey='auth.register_form.submit_error_message' values={{ error }}>
          {''}
          <a href='/login'>Login</a>
        </Trans>
      </StyledP>
    </>
  );
};

const TermsOfService = () => {
  const { setShowTermsOfService } = useAuthContext();

  return (
    <>
      <TermsP>
        <Trans i18nKey='auth.register_form.terms_of_service'>
          {''}
          <TermsButton onClick={() => setShowTermsOfService(true)}>Terms</TermsButton>
        </Trans>
      </TermsP>
    </>
  );
};

type RegisterFormData = {
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
  email?: string | null;
};

type RegisterFormProps = {
  inviteEmail: string;
  inviteToken: string;
  handleSuccessLogin: (token: string) => void;
};

const RegisterForm = ({ inviteEmail, inviteToken, handleSuccessLogin }: RegisterFormProps) => {
  const { t } = useTranslation();

  const registerSchema = useMemo(() => {
    return yup.object().shape({
      first_name: yup.string().required(t('auth.register_form.first_name.error_message') as string),
      last_name: yup.string().required(t('auth.register_form.last_name.error_message') as string),
      password: yup
        .string()
        .min(8, t('auth.register_form.password.error_message_min') as string)
        .max(256, t('auth.register_form.password.error_message_max') as string),
      password_confirmation: yup
        .string()
        .oneOf(
          [yup.ref('password')],
          t('auth.register_form.confirm_password.error_message') as string
        ),
    });
  }, [t]);

  const email = decodeURIComponent(inviteEmail);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({ resolver: yupResolver(registerSchema) });

  const [requestError, setRequestError] = useState<string | null>(null);

  const onSubmit: SubmitHandler<RegisterFormData> = async (formData) => {
    const { password } = formData;

    const requestData = {
      ...formData,
      email,
      invite_token: inviteToken,
    };

    try {
      await registerUser(requestData);
      login({ email, password }).then(({ data }) => {
        handleSuccessLogin(data.token);
        fetchUserInfo();
      });
    } catch (err) {
      setRequestError(`${(err as IRequestError).message}`);
    }
  };

  return (
    <>
      <RegisterFormHeader email={email} />
      <Spacer direction='vertical' size='xl' />

      {!requestError ? (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FlexRow>
            <Input
              autoFocus
              tw='w-full'
              type='text'
              label={t('auth.register_form.first_name.label')}
              {...register('first_name')}
              error={errors.first_name?.message}
            />

            <Input
              tw='w-full'
              type='text'
              label={t('auth.register_form.last_name.label')}
              {...register('last_name')}
              error={errors.last_name?.message}
            />
          </FlexRow>

          <Input
            type='password'
            label={t('auth.register_form.password.label')}
            {...register('password')}
            helperText='Must have at least 8 characters'
            error={errors.password?.message}
          />

          <Input
            type='password'
            label={t('auth.register_form.confirm_password.label')}
            {...register('password_confirmation')}
            error={errors.password_confirmation?.message}
          />

          <Button.Primary
            tw='bg-accent-2 text-text-and-icon-invert h-[40px] my-[12px]'
            type='submit'
            disabled={isSubmitting}
            isLoading={isSubmitting}>
            {t('auth.register_form.submit_button')}
          </Button.Primary>

          <TermsOfService />
        </StyledForm>
      ) : (
        <RegisterErrorInfo error={requestError} />
      )}
    </>
  );
};

export { RegisterForm };
